'use client';

import { useEffect, useRef, useState } from 'react';

import { useNavRouter } from '../../hooks';

interface ICountDownTimerProps {
  /**
   * The count down timer duration in seconds for the setInterval.
   */
  duration?: number;
  isPaused?: boolean;
  isVisible?: boolean;
  onTimeout: () => void;
}

/**
 * -----------------------------------------------------------------------------
 * Displays a floating countdown timer widget. default time is 30 seconds.
 */
export function CountDownTimer({
  duration = 30,
  isPaused = false,
  isVisible = true,
  onTimeout,
}: ICountDownTimerProps) {
  const { isRouting } = useNavRouter();
  const [timer, setTimer] = useState(duration);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleTimeout = () => {
    onTimeout();
  };

  useEffect(() => {
    if (isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      return undefined;
    }

    intervalRef.current = setInterval(() => {
      setTimer((prevTimer) => {
        const newTimer = prevTimer - 1;

        if (newTimer <= 0) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }

        return newTimer;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPaused]);

  useEffect(() => {
    if (timer <= 0) {
      handleTimeout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer]);

  if (isRouting) return null;

  const toggleClass = isVisible
    ? 'opacity-70'
    : 'pointer-events-none top-0 h-0 w-0 opacity-0';

  return (
    <div
      className={`
        pointer-events-none absolute left-1/2 top-4 z-50 inline-flex h-10
        -translate-x-1/2 transform items-center justify-center gap-1 rounded-3xl
        border border-gray-300 bg-white p-2 px-8 dark:border-gray-700 dark:bg-gray-700
        ${toggleClass}
      `}
    >
      {`${timer}s`}
    </div>
  );
}
